import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Tabs, Tab, Box, Tooltip, Button } from '@mui/material';
import { InfoOutlined, ViewColumn } from '@mui/icons-material';
import TabPanel from '../../../uiComponents/MuiTabs/MuiTabPanel.js';
import muiTabsStyles from '../../../uiComponents/MuiTabs/MuiTabsStyles.js';
import SearchVariantsResultsTable from '../SearchVariantsResultsTable';
import GenotypePatientsTable from './GenotypePatientsTable';
import SelectFieldsDialog from './SelectFieldsDialog';
import APIUtils from '../../../utils/APIUtils';

// Default demographic columns shown in the genotype patients table, in order.
// These are also the demographic fields the user can re-add after removing.
// Patient ID is intentionally excluded — it is the required first column.
const DEFAULT_DEMOGRAPHIC_FIELDS = [
  { id: 'patientRace', label: 'Patient Race', type: 'demographic' },
  { id: 'patientEthnicity', label: 'Patient Ethnicity', type: 'demographic' },
  { id: 'patientSex', label: 'Patient Sex', type: 'demographic' },
  { id: 'birthYear', label: 'Patient Date of Birth', type: 'demographic' },
];

// Persists the selected tab and active (selected) columns so they can be
// restored after navigating to variant details and returning via the browser
// back button. The variant results table within this flow uses its own key so
// its pagination does not collide with the standalone variant search table.
const VIEW_STATE_KEY = 'exploreGenotypeViewState';
const VARIANT_TABLE_STATE_KEY = 'exploreGenotypeVariantTable';

const getSavedViewState = () => {
  try {
    return JSON.parse(sessionStorage.getItem(VIEW_STATE_KEY)) || {};
  } catch {
    return {};
  }
};

const SearchGenotypesResults = ({
  data,
  breadCrumbs,
  onAddVariant,
  onRemoveVariant,
  addedVariantNames,
  genotypeData,
  genotypeTabEnabled,
  genotypeSearchTrigger,
  genotypeVariantSearchTrigger,
}) => {
  const classes = muiTabsStyles();
  const savedViewState = getSavedViewState();
  const [selectedTab, setSelectedTab] = useState(
    Number.isInteger(savedViewState.selectedTab) ? savedViewState.selectedTab : 0
  );
  const [activeFields, setActiveFields] = useState(
    Array.isArray(savedViewState.activeFields)
      ? savedViewState.activeFields
      : DEFAULT_DEMOGRAPHIC_FIELDS
  );
  const [selectFieldsOpen, setSelectFieldsOpen] = useState(false);
  const [analyteOptions, setAnalyteOptions] = useState([]);
  const [conditionOptions, setConditionOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  // Persist the selected tab and active columns whenever they change so the
  // view is restored on back navigation.
  useEffect(() => {
    sessionStorage.setItem(
      VIEW_STATE_KEY,
      JSON.stringify({ selectedTab, activeFields })
    );
  }, [selectedTab, activeFields]);

  // Switch to the Genotypes tab each time a genotype search is run.
  useEffect(() => {
    if (genotypeSearchTrigger > 0) {
      setSelectedTab(1);
    }
  }, [genotypeSearchTrigger]);

  // Switch back to the Variants tab each time a variant search is run, so fresh
  // variant results are shown even if the user was viewing the Genotypes tab.
  useEffect(() => {
    if (genotypeVariantSearchTrigger > 0) {
      setSelectedTab(0);
    }
  }, [genotypeVariantSearchTrigger]);

  // Never remain on the disabled Genotypes tab. The selected tab is persisted to
  // sessionStorage, so after a genotype search saved tab 1, a later fresh
  // variant search (e.g. after switching "Select Data to View" away and back)
  // would remount this component restored to tab 1 while the Genotypes tab is
  // disabled and empty - hiding the actual variant results behind an empty,
  // disabled panel that reads "No Search Results". Fall back to the Variants tab
  // whenever the Genotypes tab is not enabled.
  useEffect(() => {
    if (!genotypeTabEnabled && selectedTab === 1) {
      setSelectedTab(0);
    }
  }, [genotypeTabEnabled, selectedTab]);

  // Load the analyte and condition lookups once so the Select Fields dialog can
  // offer every possible column, regardless of whether data exists for it.
  useEffect(() => {
    let cancelled = false;
    const fetchLookups = async () => {
      setOptionsLoading(true);
      try {
        const [analytes, conditions] = await Promise.all([
          APIUtils.getAnalytesLookup(),
          APIUtils.getConditionsLookup(),
        ]);
        if (cancelled) return;
        setAnalyteOptions(Array.isArray(analytes) ? analytes : []);
        setConditionOptions(Array.isArray(conditions) ? conditions : []);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch field lookups', error);
        setAnalyteOptions([]);
        setConditionOptions([]);
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    };
    fetchLookups();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const genotypesTabLabel = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      Genotypes
      {!genotypeTabEnabled && (
        <Tooltip
          title="Search genotypes first to enable this tab"
          placement="top"
          arrow
          componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}
        >
          <span
            tabIndex={0}
            role="img"
            aria-label="Search genotypes first to enable this tab"
            style={{ display: 'flex', alignItems: 'center', pointerEvents: 'auto' }}
          >
            <InfoOutlined
              style={{
                fontSize: '1.1rem',
                color: 'var(--info-color)',
                filter: 'brightness(0.90)',
                cursor: 'pointer',
                pointerEvents: 'auto',
              }}
            />
          </span>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <div>
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        centered
        className={classes.tabs}
      >
        <Tab label="Variants" id="tab-0" aria-controls="tabpanel-0" className={classes.tab} />
        <Tab
          label={genotypesTabLabel}
          id="tab-1"
          aria-controls="tabpanel-1"
          className={classes.tab}
          disabled={!genotypeTabEnabled}
        />
      </Tabs>
      <TabPanel value={selectedTab} index={0} style={{ marginTop: '10px' }}>
        {/* Empty action bar to keep both tabs' tables aligned at the same line */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minHeight: 40, mb: 1 }} />
        <SearchVariantsResultsTable
          data={data}
          breadCrumbs={breadCrumbs}
          onAddVariant={onAddVariant}
          onRemoveVariant={onRemoveVariant}
          addedVariantNames={addedVariantNames}
          tableStateKey={VARIANT_TABLE_STATE_KEY}
        />
      </TabPanel>
      <TabPanel value={selectedTab} index={1} style={{ marginTop: '10px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minHeight: 40, mb: 1 }}>
          {Array.isArray(genotypeData) && genotypeData.length > 0 && (
            <Button variant="outlined" onClick={() => setSelectFieldsOpen(true)} startIcon={<ViewColumn fontSize='small' />}>
              Column Options
            </Button>
          )}
        </Box>
        <GenotypePatientsTable data={genotypeData} fields={activeFields} />
      </TabPanel>
      <SelectFieldsDialog
        open={selectFieldsOpen}
        onClose={() => setSelectFieldsOpen(false)}
        fields={activeFields}
        onSave={setActiveFields}
        demographicFields={DEFAULT_DEMOGRAPHIC_FIELDS}
        analyteOptions={analyteOptions}
        conditionOptions={conditionOptions}
        optionsLoading={optionsLoading}
      />
    </div>
  );
};

SearchGenotypesResults.propTypes = {
  data: PropTypes.array,
  breadCrumbs: PropTypes.array,
  onAddVariant: PropTypes.func,
  onRemoveVariant: PropTypes.func,
  addedVariantNames: PropTypes.array,
  genotypeData: PropTypes.array,
  genotypeTabEnabled: PropTypes.bool,
  genotypeSearchTrigger: PropTypes.number,
  genotypeVariantSearchTrigger: PropTypes.number,
};

export default SearchGenotypesResults;
